import { AbstractControl, ValidatorFn } from '@angular/forms';

export type PasswordStrength = 'weak' | 'medium' | 'strong';

/** Policy from API; optional so existing code can use getPasswordStrength without it. */
export interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireDigit: boolean;
  requireSpecialCharacter: boolean;
}

// Helper functions (used by both policy-based and legacy logic)
export function hasUppercase(password: string): boolean {
  return /[A-Z]/.test(password);
}

export function hasLowercase(password: string): boolean {
  return /[a-z]/.test(password);
}

export function hasNumber(password: string): boolean {
  return /[0-9]/.test(password);
}

export function hasSpecialChar(password: string): boolean {
  return /[!@#$%^&*(),.?":{}|<>+=\-_~`[\]\\';/]/.test(password);
}

/**
 * Checks if password satisfies the given policy. Returns error keys for the first failing rule.
 */
export function validatePasswordWithPolicy(password: string, policy: PasswordPolicy): { valid: true } | { valid: false; errorKey: string } {
  if (!password) {
    return { valid: false, errorKey: 'required' };
  }
  if (password.length < policy.minLength) {
    return { valid: false, errorKey: 'minLength' };
  }
  if (password.length > policy.maxLength) {
    return { valid: false, errorKey: 'maxLength' };
  }
  if (policy.requireLowercase && !hasLowercase(password)) {
    return { valid: false, errorKey: 'requireLowercase' };
  }
  if (policy.requireUppercase && !hasUppercase(password)) {
    return { valid: false, errorKey: 'requireUppercase' };
  }
  if (policy.requireDigit && !hasNumber(password)) {
    return { valid: false, errorKey: 'requireDigit' };
  }
  if (policy.requireSpecialCharacter && !hasSpecialChar(password)) {
    return { valid: false, errorKey: 'requireSpecialCharacter' };
  }
  return { valid: true };
}

/**
 * Calculates password strength from policy rules.
 * Uses policy when provided; otherwise falls back to legacy scoring (length >= 12, number, lower+upper, special).
 */
export function getPasswordStrength(password: string, policy?: PasswordPolicy | null): PasswordStrength {
  if (!password) return 'weak';

  if (policy) {
    const result = validatePasswordWithPolicy(password, policy);
    if (!result.valid) return 'weak';
    // Meets minimum → medium; more than minimum (e.g. longer) → strong
    const exceedsMin = password.length > policy.minLength;
    return exceedsMin ? 'strong' : 'medium';
  }

  // Legacy scoring
  let score = 0;
  if (hasNumber(password)) score++;
  if (hasLowercase(password) && hasUppercase(password)) score++;
  if (hasSpecialChar(password)) score++;
  if (password.length >= 12) score++;

  if (score < 3) return 'weak';
  if (score < 4) return 'medium';
  return 'strong';
}

/**
 * Validates if password is strong enough and matches confirmation.
 * When policy is provided, "strong enough" means it satisfies the policy.
 */
export function isPasswordValid(password: string, confirmPassword: string, policy?: PasswordPolicy | null): boolean {
  if (!password || !confirmPassword || password !== confirmPassword) return false;
  if (policy) {
    return validatePasswordWithPolicy(password, policy).valid;
  }
  return getPasswordStrength(password) !== 'weak';
}

/**
 * Standalone validator for password strength (legacy, no policy).
 * Prefer passwordStrengthValidatorWithPolicy when policy is available.
 */
export function passwordStrengthValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const password = control.value;
    if (!password) return null;
    const strength = getPasswordStrength(password);
    return strength === 'weak' ? { weakPassword: { value: password } } : null;
  };
}

/**
 * Standalone validator that uses the given password policy.
 */
export function passwordStrengthValidatorWithPolicy(policy: PasswordPolicy): ValidatorFn {
  return (control: AbstractControl): { [key: string]: unknown } | null => {
    const password = control.value;
    if (!password) return null;
    const result = validatePasswordWithPolicy(password, policy);
    if (result.valid) return null;
    return { weakPassword: { value: password, errorKey: result.errorKey } };
  };
}

/**
 * Standalone validator function for password matching
 */
export function passwordMatchValidator(): ValidatorFn {
  return (group: AbstractControl): { [key: string]: unknown } | null => {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    const confirmPasswordControl = group.get('confirmPassword');

    // Only validate if confirmPassword has a value or has been touched
    if (confirmPasswordControl && (confirmPassword || confirmPasswordControl.touched)) {
      if (password && confirmPassword && password !== confirmPassword) {
        // Set error on confirmPassword control so FormElementComponent can display it
        const existingErrors = confirmPasswordControl.errors || {};
        confirmPasswordControl.setErrors({ ...existingErrors, passwordMismatch: true });
        return null; // Don't return form-level error
      } else if (password && confirmPassword && password === confirmPassword) {
        // Clear the passwordMismatch error if passwords match
        const errors = confirmPasswordControl.errors;
        if (errors && errors['passwordMismatch']) {
          delete errors['passwordMismatch'];
          const hasOtherErrors = Object.keys(errors).length > 0;
          confirmPasswordControl.setErrors(hasOtherErrors ? errors : null);
        }
      }
    }
    return null;
  };
}
